// FLAGS: health-data, clinical, silent-failure
// The expert's side of a live therapy/coaching session: joins the Azure Communication Services room and
// chat for one consult and starts server-side transcription of it.
import {
  AzureCommunicationTokenCredential,
  CommunicationUserIdentifier,
} from "@azure/communication-common";
import {
  fromFlatCommunicationIdentifier,
  CallWithChatComposite,
  COMPOSITE_LOCALE_EN_GB,
  onResolveVideoEffectDependency,
  CallWithChatAdapter,
  CallWithChatAdapterState,
  createAzureCommunicationCallWithChatAdapterFromClients,
  createStatefulCallClient,
  createStatefulChatClient,
} from "@azure/communication-react";
import {
  WithSnackbarProps,
  withSnackbar,
} from "@src/components/SnackBarComponent";
import { RESPONSE_STATUS } from "@src/constants";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getToken, whoElseIsInCall } from "@src/queries/video-calling";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAcsFileUploadOptions } from "@src/utils/acsFileUploadHandler";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { initializeIcons } from "@uifabric/icons";
import { connectTranscription, getConsultById } from "@src/queries/consults";
import { acsDownloadOptions } from "@src/utils/acsFileDownloadHandler";
import useSound from "use-sound";
import joinedSound from "@src/resources/audio/participant-joined.wav";
import { Call } from "@azure/communication-calling";
import {
  ResponseAzureCommunicationServiceToken,
  ResponseConsultsConsultWithNotesResponse,
} from "@src/api/v1/data-contracts";

initializeIcons();

const MakePsychologistCall: React.FC<WithSnackbarProps> = ({
  snackbarShowMessage,
}) => {
  const { consultId, clientId } = useParams();
  const { t } = useTranslation();
  const [token, setToken] =
    useState<ResponseAzureCommunicationServiceToken | null>();
  const [inCallNames, setInCallNames] = useState<string | undefined>(undefined);
  const [showInCallNames, setShowInCallNames] = useState(false);
  const appInsights = useAppInsightsContext();
  const [play] = useSound(joinedSound);
  const isDefaultConfigSet = useRef(false);
  const [consult, setConsult] = useState<
    ResponseConsultsConsultWithNotesResponse | undefined
  >(undefined);
  const [callAdapter, setCallAdapter] = useState<
    CallWithChatAdapter | undefined
  >(undefined);

  const fixVideoPreviewMirroring = () => {
    const el = document.querySelector(
      '[data-ui-id="call-composite-local-preview"], [data-ui-id="local-video-tile"]',
    );
    const videoElement = el?.querySelector("video");
    if (
      videoElement?.style.transform === "scaleX(-1)" &&
      videoElement?.parentElement?.style.transform === "rotateY(180deg)"
    ) {
      videoElement.style.transform = "";
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!clientId || !consultId) return undefined;
      try {
        const response = await getConsultById(clientId, Number(consultId));
        setConsult(response);
        appInsights.trackEvent({
          name: "ACS_ConsultFetched",
          properties: { consultId, clientId, roomId: response?.roomId },
        });
        return response;
      } catch (error) {
        appInsights.trackException({
          exception:
            error instanceof Error
              ? error
              : new Error("Failed to fetch consult"),
          properties: { consultId, clientId },
        });
        throw error;
      }
    };

    fetchData();
  }, [clientId, consultId, appInsights]);

  useEffect(() => {
    (async () => {
      if (!consult?.roomId) return;
      const getTokenTask = getToken(consult.roomId)?.catch((e: any) => {
        appInsights.trackException({
          exception:
            e instanceof Error ? e : new Error("Failed to fetch ACS token"),
          properties: {
            consultId,
            roomId: consult.roomId,
            status: e.response?.status,
          },
        });
        if (e.response?.status === RESPONSE_STATUS.BAD_REQUEST) {
          snackbarShowMessage(e.response, "error");
        }
        return undefined;
      });
      const whoElseIsInCallTask = Promise.resolve(
        whoElseIsInCall(consult.roomId),
      ).catch((e: any) => {
        appInsights.trackException({
          exception:
            e instanceof Error
              ? e
              : new Error("Failed to fetch who else in call"),
          properties: { consultId, roomId: consult.roomId },
        });
        return [];
      });
      const result = await Promise.all([getTokenTask, whoElseIsInCallTask]);

      setToken(result[0]);
      setInCallNames(result[1]?.map((x: any) => x.name).join(", "));
      setShowInCallNames(true);

      if (result[0]) {
        appInsights.trackEvent({
          name: "ACS_TokenFetched",
          properties: { consultId, roomId: consult.roomId },
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consult?.roomId, appInsights, consultId, snackbarShowMessage]);

  useEffect(() => {
    if (consult?.canJoinVideoCall === false) {
      snackbarShowMessage(t("MakePsychologistCall.ExpiredSession"), "error");
    }
  }, [consult?.canJoinVideoCall, snackbarShowMessage, t]);

  // A well-formed token is required to initialize the chat and calling adapters.
  const credential = useMemo(() => {
    if (!token?.token) return undefined;
    try {
      return new AzureCommunicationTokenCredential(token.token);
    } catch (err) {
      appInsights.trackException({
        exception:
          err instanceof Error ? err : new Error("Failed to create credential"),
      });
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // FLAGS: health-data, silent-failure
  // Once the call connects this asks the API to attach transcription to it, so what is said in the
  // session becomes stored text. A failure is reported only to App Insights: the call carries on
  // untranscribed and neither expert nor client is told, which also silently removes the input the
  // "Autofill with AI" button in SessionNotesPaper depends on.
  const subscribeAdapterEvents = useCallback(
    async (
      callWithChatAdapter: CallWithChatAdapter,
      callAgent: any,
    ): Promise<CallWithChatAdapter> => {
      callWithChatAdapter.onStateChange(
        async (state: CallWithChatAdapterState) => {
          appInsights.trackEvent({
            name: "ACS_StateChange",
            properties: {
              consultId,
              page: state.page,
              callState: state.call?.state,
            },
          });

          switch (state.page) {
            case "configuration":
              if (!isDefaultConfigSet.current) {
                isDefaultConfigSet.current = true;
                await callWithChatAdapter?.unmute();
                await callWithChatAdapter?.startCamera();
              }
              break;
            case "lobby":
              if (state.call) {
                setShowInCallNames(false);
              }
              break;
            default:
              break;
          }
          setTimeout(() => {
            fixVideoPreviewMirroring();
          }, 50);
        },
      );
      callWithChatAdapter.on("callParticipantsJoined", () => {
        appInsights.trackEvent({
          name: "ACS_ParticipantJoined",
          properties: { consultId },
        });
        play();
      });

      callAgent.on(
        "callsUpdated",
        (e: { added: Call[]; removed: Call[] }): void => {
          appInsights.trackEvent({
            name: "ACS_CallsUpdated",
            properties: {
              consultId,
              addedCount: e.added.length,
              removedCount: e.removed.length,
            },
          });

          const handleStateChanged = (addedCall: Call) => async () => {
            if (addedCall.state === "Connected") {
              try {
                const serverCallId = await addedCall.info.getServerCallId();
                appInsights.trackEvent({
                  name: "ACS_CallConnected",
                  properties: { consultId, serverCallId },
                });
                await connectTranscription(Number(consultId), serverCallId);
              } catch (error) {
                appInsights.trackException({
                  exception:
                    error instanceof Error
                      ? error
                      : new Error("Failed to connect transcription"),
                  properties: { consultId },
                });
              }
            }
            fixVideoPreviewMirroring();
          };
          e.added.forEach((addedCall) => {
            addedCall.on("stateChanged", handleStateChanged(addedCall));
          });
        },
      );

      return callWithChatAdapter;
    },
    [play, consultId, appInsights],
  );

  const existingBackgroundImagesCount = 15;

  useEffect(() => {
    let disposed = false;
    let currentAdapter: CallWithChatAdapter | undefined;
    let currentCallAgent: any;
    let currentChatClient: any;

    (async () => {
      if (!token || !consult || !credential) return;

      try {
        appInsights.trackEvent({
          name: "ACS_AdapterCreationStarted",
          properties: {
            consultId,
            roomId: consult.roomId,
            chatThreadId: consult.chatThreadId,
          },
        });

        const userId = fromFlatCommunicationIdentifier(
          token.communicationUserId!,
        ) as CommunicationUserIdentifier;

        const callAdapterOptions = {
          videoBackgroundOptions: {
            videoBackgroundImages: Array.from(
              { length: existingBackgroundImagesCount },
              (_, index) => {
                const imageNameLength = 3;
                const key = (index + 1)
                  .toString()
                  .padStart(imageNameLength, "0");
                return {
                  key,
                  url: `/images/call-backgrounds/${key}.jpg`,
                };
              },
            ),
            onResolveDependency: onResolveVideoEffectDependency,
          },
        };
        const callClient = createStatefulCallClient({ userId });
        const callAgent = await callClient.createCallAgent(credential, {
          displayName: token.firstName ?? "",
        });
        currentCallAgent = callAgent;
        const chatClient = createStatefulChatClient({
          userId,
          credential,
          endpoint: token.endpointUrl!,
          displayName: token.firstName ?? "",
        });
        currentChatClient = chatClient;
        await chatClient.startRealtimeNotifications();
        const chatThreadClient = chatClient.getChatThreadClient(
          consult.chatThreadId!,
        );

        let adapter =
          await createAzureCommunicationCallWithChatAdapterFromClients({
            callClient,
            callAgent,
            callLocator: {
              roomId: consult.roomId!,
            },
            chatClient,
            chatThreadClient,
            callAdapterOptions,
          });

        adapter = await subscribeAdapterEvents(adapter, callAgent);

        if (disposed) {
          adapter.dispose();
          callAgent.dispose();
          chatClient.dispose();
          return;
        }

        currentAdapter = adapter;
        setCallAdapter(adapter);

        appInsights.trackEvent({
          name: "ACS_AdapterCreationSuccess",
          properties: { consultId, roomId: consult.roomId },
        });
      } catch (error) {
        if (disposed) return;
        appInsights.trackException({
          exception:
            error instanceof Error
              ? error
              : new Error("Failed to create adapter"),
          properties: {
            consultId,
            roomId: consult?.roomId,
            chatThreadId: consult?.chatThreadId,
          },
        });
      }
    })();

    return () => {
      disposed = true;
      currentAdapter?.dispose();
      currentCallAgent?.dispose();
      currentChatClient?.dispose();
    };
  }, [
    credential,
    consult?.roomId,
    consult?.chatThreadId,
    subscribeAdapterEvents,
    appInsights,
    consultId,
  ]);

  return (
    consult?.canJoinVideoCall && (
      <div className="h-screen flex">
        <div className="flex-grow flex-shrink">
          {callAdapter &&
            showInCallNames &&
            (inCallNames ? (
              <div className="flex justify-center absolute top-8 mt-4 z-10 left-1/2 transform -translate-x-1/2 items-center gap-2 body2">
                <div className="rounded-full bg-slate-200 text-slate-700 w-10 h-10 flex justify-center items-center cap1">
                  {inCallNames[0].toUpperCase()}
                </div>
                {`${inCallNames} ${t("MakePsychologistCall.ClientIsInCall")}`}
              </div>
            ) : (
              <div className="flex justify-center absolute top-8 mt-4 z-10 left-1/2 transform -translate-x-1/2 items-center gap-2 body2">
                {t("MakePsychologistCall.NoClientJoined")}
              </div>
            ))}
          {callAdapter && (
            <CallWithChatComposite
              adapter={callAdapter}
              locale={COMPOSITE_LOCALE_EN_GB}
              options={{
                attachmentOptions: {
                  uploadOptions: getAcsFileUploadOptions(
                    consult.chatThreadId!,
                    t("MakePsychologistCall.UploadFailed"),
                  ),
                  downloadOptions: acsDownloadOptions,
                },
              }}
            />
          )}
          {token && credential === null && (
            <h3>
              Failed to construct credential. Provided token is malformed.
            </h3>
          )}
          {(!callAdapter || !credential) && <h3>Initializing...</h3>}
        </div>
      </div>
    )
  );
};

export default withSnackbar(MakePsychologistCall);
